import {
  logHttpError,
  logHttpRequest,
  logHttpResponse,
  requestIdFrom,
} from '@/shared/utils/httpLogger'
import { ApiError } from './types'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

// Request deduplication: track in-flight requests by key
const inFlightRequests = new Map<string, Promise<any>>()

// Generate a unique key for request deduplication
function getRequestKey(endpoint: string, method: string, body?: any): string {
  const bodyHash = body ? JSON.stringify(body) : ''
  return `${method}:${endpoint}:${bodyHash}`
}

// Custom fetch wrapper with interceptors
class ApiClient {
  private baseURL: string
  private getAuthToken: () => Promise<string | null>

  constructor(baseURL: string, getAuthToken: () => Promise<string | null>) {
    this.baseURL = baseURL
    this.getAuthToken = getAuthToken
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOn401: boolean = true
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`
    const method = options.method || 'GET'
    
    // Parse body for deduplication key (only for non-GET requests)
    let bodyForKey: any = undefined
    if (options.body && method !== 'GET') {
      try {
        if (options.body instanceof FormData) {
          // For FormData, use a hash of the endpoint + method (can't stringify FormData)
          bodyForKey = `formdata:${endpoint}`
        } else {
          bodyForKey = typeof options.body === 'string' ? JSON.parse(options.body) : options.body
        }
      } catch {
        // If parsing fails, use the body as-is (string)
        bodyForKey = options.body
      }
    }
    
    // Check for duplicate in-flight request
    const requestKey = getRequestKey(endpoint, method, bodyForKey)
    const existingRequest = inFlightRequests.get(requestKey)
    
    if (existingRequest) {
      // Return existing promise for duplicate request
      return existingRequest as Promise<T>
    }
    
    // Create new request promise
    const requestPromise = this.executeRequest<T>(url, endpoint, options, retryOn401, method)
    
    // Store in-flight request
    inFlightRequests.set(requestKey, requestPromise)
    
    // Clean up after request completes
    requestPromise.finally(() => {
      inFlightRequests.delete(requestKey)
    })
    
    return requestPromise
  }

  private async executeRequest<T>(
    url: string,
    endpoint: string,
    options: RequestInit,
    retryOn401: boolean,
    method: string
  ): Promise<T> {
    const logPath = endpoint
    const startMs = Date.now()
    logHttpRequest(method, logPath)

    // Add authentication header
    const token = await this.getAuthToken()
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    }

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      headers,
      // Pass cache option if provided (for cache-busting)
      cache: options.cache,
    }

    try {
      const response = await fetch(url, config)
      const durationMs = Date.now() - startMs
      const requestId = requestIdFrom(response)

      // Handle 401 Unauthorized - token may have expired, try to refresh and retry once
      if (response.status === 401 && retryOn401) {
        // Attempt to refresh token by calling getAuthToken again (it will refresh if expired)
        const newToken = await this.getAuthToken()
        
        // If we got a new token (different from the original), retry the request once
        if (newToken && newToken !== token) {
          const retryHeaders: Record<string, string> = {
            ...(options.headers as Record<string, string> || {}),
          }
          
          // Don't set Content-Type for FormData - let the browser set it with boundary
          if (!(options.body instanceof FormData)) {
            retryHeaders['Content-Type'] = 'application/json'
          }
          
          retryHeaders.Authorization = `Bearer ${newToken}`
          
          // Reuse the original body (FormData can be reused in fetch)
          const retryConfig: RequestInit = {
            ...options,
            headers: retryHeaders,
            body: options.body, // FormData can be reused
          }
          
          const retryResponse = await fetch(url, retryConfig)
          
          if (!retryResponse.ok) {
            const errorData = await this.parseErrorResponse(retryResponse)
            const errorMessage = errorData.message || errorData.error || `HTTP ${retryResponse.status}`
            logHttpError(
              method,
              logPath,
              retryResponse.status,
              Date.now() - startMs,
              String(errorMessage),
              requestIdFrom(retryResponse),
            )
            throw new ApiError(
              errorMessage,
              retryResponse.status,
              errorData.errors
            )
          }

          // Parse successful retry response
          if (retryResponse.status === 204) {
            logHttpResponse(method, logPath, 204, Date.now() - startMs, {
              requestId: requestIdFrom(retryResponse),
            })
            return {} as T
          }

          const retryData = await retryResponse.json()
          logHttpResponse(method, logPath, retryResponse.status, Date.now() - startMs, {
            requestId: requestIdFrom(retryResponse),
            body: retryData,
          })
          return retryData
        } else {
          // Token refresh failed or token is still invalid
          // For login endpoints, this is a legitimate authentication failure
          const errorData = await this.parseErrorResponse(response)
          
          // Extract error message - handle both string and object formats
          let errorMessage = 'Authentication failed. Please sign in again.'
          if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.error) {
            if (typeof errorData.error === 'string') {
              errorMessage = errorData.error
            } else if (errorData.error?.message) {
              errorMessage = errorData.error.message
            }
          }
          
          logHttpError(method, logPath, response.status, durationMs, errorMessage, requestId)
          throw new ApiError(
            errorMessage,
            response.status,
            errorData.errors
          )
        }
      }

      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response)
        
        // Rate limiting — prefer backend message so Ranger-specific copy comes through
        if (response.status === 429) {
          let rateLimitMessage = 'Too many requests. Please wait a moment and try again.'
          if (errorData.message) {
            rateLimitMessage = errorData.message
          } else if (typeof errorData.error === 'string' && errorData.error) {
            rateLimitMessage = errorData.error
          } else if (errorData.error?.message) {
            rateLimitMessage = errorData.error.message
          }
          throw new ApiError(rateLimitMessage, response.status, errorData.errors)
        }
        
        // Extract error message from different response formats
        // Backend returns: { success: false, error: "..." } for auth errors
        // Or sometimes: { success: false, error: { message: "...", code: "...", ... } }
        // Or: { success: false, message: "..." }
        let errorMessage = `HTTP ${response.status}`
        
        // Priority: message field first, then error field
        if (errorData.message) {
          errorMessage = errorData.message
        } else if (errorData.error) {
          // Handle both string and object error formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error
          } else if (errorData.error?.message) {
            errorMessage = errorData.error.message
          } else {
            // Fallback: try to stringify the error object nicely
            errorMessage = JSON.stringify(errorData.error)
          }
        }
        
        logHttpError(method, logPath, response.status, durationMs, errorMessage, requestId)
        throw new ApiError(
          errorMessage,
          response.status,
          errorData.errors
        )
      }

      // Parse successful response
      if (response.status === 204) {
        logHttpResponse(method, logPath, 204, durationMs, { requestId })
        return {} as T // No content
      }

      const data = await response.json()
      logHttpResponse(method, logPath, response.status, durationMs, {
        requestId,
        body: data,
      })
      return data
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      logHttpError(
        method,
        logPath,
        null,
        Date.now() - startMs,
        error instanceof Error ? error.message : 'Network error',
      )

      // Network or parsing errors
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0
      )
    }
  }

  private async parseErrorResponse(response: Response): Promise<any> {
    try {
      return await response.json()
    } catch {
      return { message: `HTTP ${response.status}` }
    }
  }

  // HTTP methods
  async get<T>(endpoint: string, params?: Record<string, any>, options?: { cache?: RequestCache }): Promise<T> {
    let queryString = ''
    if (params) {
      // Filter out undefined, null, and empty string values
      const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value
        }
        return acc
      }, {} as Record<string, any>)
      
      if (Object.keys(filteredParams).length > 0) {
        queryString = `?${new URLSearchParams(filteredParams).toString()}`
      }
    }
    return this.request<T>(`${endpoint}${queryString}`, { cache: options?.cache })
  }

  async post<T>(endpoint: string, data?: any, options?: { signal?: AbortSignal }): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
      signal: options?.signal,
    })
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    })
  }
}

// Create API client instance
export const createApiClient = (getAuthToken: () => Promise<string | null>) => {
  return new ApiClient(API_BASE_URL, getAuthToken)
}

// Create public API client for guest operations (no auth required)
export const createPublicApiClient = () => {
  return new ApiClient(API_BASE_URL, async () => null)
}

// Default export for convenience
export default ApiClient
